import React, { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { Location } from '../types';
import { locationsService } from '../services';
import './LocationSelector.css';
import { getLocationPath, sortLocationsByPath } from '../utils/locationPaths';

interface LocationSelectorProps {
  facilityId: string;
  refreshKey?: number;
  value?: string; // location_id
  onChange: (locationId: string | undefined) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  facilityId,
  refreshKey = 0,
  value,
  onChange,
  error,
  required = false,
  placeholder = "Select location..."
}) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>();

  useEffect(() => {
    if (facilityId) {
      loadLocations();
    }
  }, [facilityId, refreshKey]);

  useEffect(() => {
    if (value && locations.length > 0) {
      const location = locations.find(loc => loc.id === value);
      setSelectedLocation(location);
    } else {
      setSelectedLocation(undefined);
    }
  }, [value, locations]);

  const loadLocations = async () => {
    setLoading(true);
    try {
      const data = await locationsService.getFacilityLocations(facilityId);
      setLocations(data);
    } catch (err) {
      console.error('Failed to load locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const locationId = e.target.value;
    onChange(locationId === '' ? undefined : locationId);
  };

  return (
    <div className="location-selector-wrapper">
      <div className={`location-selector-container ${error ? 'has-error' : ''}`}>
        <div className="location-selector-icon">
          <MapPin size={18} />
        </div>

        <select
          aria-label="Location"
          value={value || ''}
          onChange={handleChange}
          className="location-selector-select"
          disabled={loading}
          required={required}
        >
          <option value="">{loading ? 'Loading locations...' : placeholder}</option>

          {sortLocationsByPath(locations).map(location => (
            <option key={location.id} value={location.id}>
              {getLocationPath(location, locations)} ({location.type})
            </option>
          ))}

          {locations.length === 0 && !loading && (
            <option disabled>No locations defined for this facility</option>
          )}
        </select>
      </div>

      {error && <span className="location-selector-error">{error}</span>}

      {selectedLocation && (
        <div className="location-selector-preview">
          <span className="location-type-badge">{selectedLocation.type}</span>
          <span className="location-path">{getLocationPath(selectedLocation, locations)}</span>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
